// Auth event system for managing session state across the app
// Uses a simple pub/sub pattern to decouple axios from React components

class AuthEventEmitter {
  constructor() {
    this.listeners = {
      sessionExpired: [],
      sessionRefreshed: [],
      authModalClosed: [],
      authError: [],
    };
    this.isSessionExpired = false;
    this.pendingRequests = [];
  }

  // Subscribe to an event
  on(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].push(callback);
    }
    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  // Unsubscribe from an event
  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  // Emit an event
  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in ${event} listener:`, error);
        }
      });
    }
  }

  // Mark session as expired and notify listeners
  notifySessionExpired(error) {
    if (!this.isSessionExpired) {
      this.isSessionExpired = true;
      this.emit('sessionExpired', { error });
    }
  }

  // Mark session as refreshed and retry pending requests
  notifySessionRefreshed() {
    this.isSessionExpired = false;
    this.emit('sessionRefreshed', {});

    // Retry any pending requests
    const requests = [...this.pendingRequests];
    this.pendingRequests = [];
    requests.forEach(({ resolve, config }) => {
      resolve(config);
    });
  }

  // Add a request to retry after session refresh
  addPendingRequest(config) {
    return new Promise((resolve, reject) => {
      this.pendingRequests.push({ resolve, reject, config });
    });
  }

  // Clear pending requests (e.g., on full logout)
  clearPendingRequests() {
    this.pendingRequests.forEach(({ reject }) => {
      reject(new Error('Session cleared'));
    });
    this.pendingRequests = [];
  }

  // Reset state (e.g., after successful login)
  reset() {
    this.isSessionExpired = false;
    this.clearPendingRequests();
  }
}

// Singleton instance
const authEvents = new AuthEventEmitter();

export default authEvents;
